import { Box, Button, Card, Flex, Loader, NumberInput, Select, Stack, Text } from '@mantine/core';
import { GetEnergyConsumptionResponse } from 'api/actions/get-energy-consumption/get-energy-consumption-response';
import { GetMonthlyEnergyStatsResponse } from 'api/actions/get-monthly-energy-stats/get-monthly-energy-stats-response';
import { GetSensorResponse } from 'api/actions/get-sensor/get-sensor-response';
import { useApi } from 'api/api-context';
import { format, subDays, subMonths, startOfMonth } from 'date-fns';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  TooltipItem,
} from 'chart.js';
import theme from 'theme';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Regular from 'theme/components/Typography/P4Regular';
import H1Medium from 'theme/components/Typography/H1Medium';
import { IconLock, IconWallet, IconTrendingUp } from '@tabler/icons-react';
import useLang from 'lang';
import { withBrowserTimezone } from 'env/browser-timezone';

const DEFAULT_FIXED_TARIFF_EUR_PER_MWH = 200;
const DEFAULT_FIXED_PRICE_EUR_PER_KWH = 0.2;

/** Convert backend tariff format (€/MWh) to UI format (€/kWh). */
function convertPricePerMwhToPerKwh(pricePerMwh: number) {
  return pricePerMwh / 1000;
}

/** Convert UI tariff format (€/kWh) to backend format (€/MWh). */
function convertPricePerKwhToPerMwh(pricePerKwh: number) {
  return pricePerKwh * 1000;
}

/** Ranges available in Tuya device mode */
type TuyaRange = 'dnes' | 'tyzden' | 'mesiac' | 'minuly-mesiac' | 'polrok' | 'rok';
/** Ranges available in sensor mode (Measuring Type ID 17) */
type SensorRange =
  | 'dnes'
  | 'vcera'
  | 'tento-tyzden'
  | 'tento-mesiac'
  | 'poslednych-12-mesiacov'
  | 'minuly-tyzden'
  | 'minuly-mesiac'
  | 'minuly-rok';
type Range = TuyaRange | SensorRange;

ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend);

interface EnergySpotComparisonProps {
  /** Energy consumption data for the selected day (Tuya device mode) */
  data?: GetEnergyConsumptionResponse;
  /** Currently selected date for display label */
  selectedDate?: Date | null;
  /** Tuya device ID used for weekly/monthly API calls */
  deviceId?: number;
  /** Energy comparison data from sensor (Measuring Type ID 17) */
  energyComparison?: GetSensorResponse['sensor']['energyComparison'];
  /** Sensor ID used to request backend recalculation for fixed tariff changes */
  sensorId?: number;
}

interface SensorBreakdownItem {
  start?: string;
  end?: string;
  label?: string;
  consumption?: number;
  spotCost?: number;
  fixedCost?: number;
  difference?: number;
}

interface SensorRangeData {
  granularity?: '15min' | 'day' | 'month';
  breakdown?: SensorBreakdownItem[];
  totalConsumption?: number;
  totalSpotCost?: number;
  totalFixedCost?: number;
  totalDifference?: number;
}

/**
 * EnergySpotComparison - displays consumption bar chart and SPOT vs FIX cost comparison.
 *
 * Two modes:
 *   1. Tuya device mode (data + deviceId): works with Smart Breaker data
 *   2. Sensor mode (energyComparison): works with Chastia sensor Measuring Type ID 17 data
 *
 * Tuya mode uses the legacy power-device ranges, while sensor mode uses backend-defined
 * comparison ranges for today, yesterday, this week/last week, this month/last month,
 * and the last 12 months.
 */
export default function EnergySpotComparison({
  data,
  selectedDate,
  deviceId,
  energyComparison,
  sensorId,
}: EnergySpotComparisonProps) {
  const { getAction } = useApi();
  const { _t } = useLang();

  // Detect mode
  const isSensorMode = !!energyComparison;
  const [sensorEnergyComparison, setSensorEnergyComparison] = useState(energyComparison);
  const [fixedPriceInput, setFixedPriceInput] = useState<number | string>(
    isSensorMode
      ? convertPricePerMwhToPerKwh(energyComparison?.fixedTariff ?? DEFAULT_FIXED_TARIFF_EUR_PER_MWH)
      : DEFAULT_FIXED_PRICE_EUR_PER_KWH
  );
  const [fixedPriceActive, setFixedPriceActive] = useState<number>(
    isSensorMode
      ? convertPricePerMwhToPerKwh(energyComparison?.fixedTariff ?? DEFAULT_FIXED_TARIFF_EUR_PER_MWH)
      : DEFAULT_FIXED_PRICE_EUR_PER_KWH
  );
  const [range, setRange] = useState<Range>('dnes');
  const [rangeStats, setRangeStats] = useState<GetMonthlyEnergyStatsResponse | null>(null);
  const [isLoadingRange, setIsLoadingRange] = useState(false);
  const [isLoadingSensorTariff, setIsLoadingSensorTariff] = useState(false);

  useEffect(() => {
    setSensorEnergyComparison(energyComparison);

    if (isSensorMode) {
      const fixedPricePerKwh = convertPricePerMwhToPerKwh(
        energyComparison?.fixedTariff ?? DEFAULT_FIXED_TARIFF_EUR_PER_MWH
      );
      setFixedPriceInput(fixedPricePerKwh);
      setFixedPriceActive(fixedPricePerKwh);
    }
  }, [energyComparison, isSensorMode]);

  // ── Fetch weekly / monthly stats when range changes (Tuya mode only) ────────
  useEffect(() => {
    if (isSensorMode || range === 'dnes') {
      setRangeStats(null);
      return;
    }
    setIsLoadingRange(true);
    const todayEnv = new Date();

    let query: Record<string, string | number>;
    if (range === 'tyzden') {
      query = { from: format(subDays(todayEnv, 6), 'yyyy-MM-dd'), to: format(todayEnv, 'yyyy-MM-dd') };
    } else if (range === 'mesiac') {
      query = { month: format(todayEnv, 'yyyy-MM') };
    } else if (range === 'minuly-mesiac') {
      const lastMonth = subMonths(todayEnv, 1);
      query = { month: format(lastMonth, 'yyyy-MM') };
    } else if (range === 'polrok') {
      query = { from: format(startOfMonth(subMonths(todayEnv, 5)), 'yyyy-MM-dd'), to: format(todayEnv, 'yyyy-MM-dd') };
    } else {
      // rok
      query = { from: format(startOfMonth(subMonths(todayEnv, 11)), 'yyyy-MM-dd'), to: format(todayEnv, 'yyyy-MM-dd') };
    }

    const action = getAction('GetMonthlyEnergyStats') as any;
    action({ parameters: { deviceId }, query })
      .then((res: GetMonthlyEnergyStatsResponse) => {
        setRangeStats(res);
        setIsLoadingRange(false);
      })
      .catch(() => {
        setRangeStats(null);
        setIsLoadingRange(false);
      });
  }, [range, deviceId, isSensorMode]);

  // ── Tuya "Dnes" derived values (15-min intervals) ────────────────────────
  const spotPrices = data?.spotPrices || [];
  const validIntervals = useMemo(
    () => (data?.intervals || []).filter((i) => i.consumption !== null),
    [data?.intervals]
  );

  const dayLabels = useMemo(() => validIntervals.map((i) => i.label), [validIntervals]);
  const dayTotalConsumption = useMemo(
    () => validIntervals.reduce((sum, i) => sum + (i.consumption || 0), 0),
    [validIntervals]
  );

  /** Spot cost per 15-min interval in € (Tuya day) */
  const daySpotCosts = useMemo(() => {
    if (spotPrices.length === 0 || !data) return null;
    return validIntervals.map((interval) => {
      const [hours, minutes] = interval.label!.split(':').map(Number);
      const intervalStart = new Date(
        Date.UTC(
          new Date(data.date).getUTCFullYear(),
          new Date(data.date).getUTCMonth(),
          new Date(data.date).getUTCDate(),
          hours,
          minutes,
          0,
          0
        )
      );
      const matchingPrice = spotPrices.find((sp) => {
        const spStart = new Date(sp.deliveryStart!);
        const spEnd = new Date(sp.deliveryEnd!);
        return intervalStart >= spStart && intervalStart < spEnd;
      });
      if (!matchingPrice?.price) return 0;
      return (interval.consumption! * matchingPrice.price) / 1_000_000;
    });
  }, [validIntervals, spotPrices, data?.date]);

  /** Fix cost per 15-min interval in € (Tuya day) */
  const dayFixCosts = useMemo(
    () => validIntervals.map((i) => (i.consumption! * fixedPriceActive) / 1_000),
    [validIntervals, fixedPriceActive]
  );

  // ── Sensor comparison derived values (universal breakdown) ───────────────
  const sensorRangeData = useMemo((): SensorRangeData | null => {
    if (!sensorEnergyComparison) return null;
    const key = range as SensorRange;
    return (sensorEnergyComparison[key] || null) as SensorRangeData | null;
  }, [sensorEnergyComparison, range]);

  const sensorBreakdown = useMemo(() => sensorRangeData?.breakdown || [], [sensorRangeData]);
  const sensorGranularity =
    sensorRangeData?.granularity ||
    (range === 'dnes' || range === 'vcera' ? '15min' : range === 'poslednych-12-mesiacov' ? 'month' : 'day');
  const sensorLabels = useMemo(
    () =>
      sensorBreakdown.map((row) => {
        if (sensorGranularity === '15min') return row.label || '';
        if (sensorGranularity === 'month') return row.label || '';
        if (row.label) {
          const [, month, day] = row.label.split('-');
          if (month && day) return `${Number(day)}.${Number(month)}.`;
        }
        return row.label || '';
      }),
    [sensorBreakdown, sensorGranularity]
  );
  const sensorConsumptionData = useMemo(() => sensorBreakdown.map((row) => row.consumption || 0), [sensorBreakdown]);
  const sensorSpotCosts = useMemo(() => sensorBreakdown.map((row) => row.spotCost || 0), [sensorBreakdown]);
  const sensorFixedCosts = useMemo(() => sensorBreakdown.map((row) => row.fixedCost || 0), [sensorBreakdown]);
  const sensorTotalConsumption = useMemo(
    () => sensorRangeData?.totalConsumption ?? sensorBreakdown.reduce((sum, row) => sum + (row.consumption || 0), 0),
    [sensorRangeData, sensorBreakdown]
  );

  // ── Tuya weekly/monthly derived values ───────────────────────────────────
  const dailySavings = rangeStats?.dailySavings ?? [];

  const rangeLabels = useMemo(
    () =>
      dailySavings.map((d) => {
        if (!d.date) return '';
        const dt = new Date(d.date);
        return `${dt.getUTCDate()}.${dt.getUTCMonth() + 1}.`;
      }),
    [dailySavings]
  );
  const rangeTotalConsumption = useMemo(
    () => dailySavings.reduce((sum, d) => sum + (d.consumption || 0), 0),
    [dailySavings]
  );
  const rangeSpotCosts = useMemo(() => dailySavings.map((d) => d.spotCost ?? 0), [dailySavings]);
  const rangeFixCosts = useMemo(
    () => dailySavings.map((d) => ((d.consumption ?? 0) * fixedPriceActive) / 1_000),
    [dailySavings, fixedPriceActive]
  );

  // ── Active values (dispatched on mode & selected range) ────────────────
  // Both 'dnes' and 'vcera' use 15-min interval charts (not daily bar charts)
  const isDay = range === 'dnes' || range === 'vcera';
  const labels = isSensorMode ? sensorLabels : isDay ? dayLabels : rangeLabels;
  const totalConsumption = isSensorMode ? sensorTotalConsumption : isDay ? dayTotalConsumption : rangeTotalConsumption;
  const spotCosts = isSensorMode ? sensorSpotCosts : isDay ? daySpotCosts : rangeSpotCosts;
  const fixCosts = isSensorMode ? sensorFixedCosts : isDay ? dayFixCosts : rangeFixCosts;
  const hasSpot = isSensorMode
    ? sensorSpotCosts.some((c: number) => c > 0)
    : isDay
      ? spotPrices.length > 0
      : rangeSpotCosts.some((c: number) => c > 0);

  /** Cumulative spot costs for line chart (all ranges) */
  const cumulativeSpotCosts = useMemo(() => {
    if (!spotCosts) return [];
    let sum = 0;
    return spotCosts.map((c) => {
      sum += c;
      return sum;
    });
  }, [spotCosts]);

  /** Cumulative fix costs for line chart (all ranges) */
  const cumulativeFixCosts = useMemo(() => {
    let sum = 0;
    return fixCosts.map((c) => {
      sum += c;
      return sum;
    });
  }, [fixCosts]);

  const totalSpotCost = useMemo(
    () =>
      isSensorMode
        ? sensorRangeData?.totalSpotCost ?? spotCosts?.reduce((sum, cost) => sum + cost, 0) ?? null
        : spotCosts
          ? spotCosts.reduce((sum, cost) => sum + cost, 0)
          : null,
    [isSensorMode, sensorRangeData, spotCosts]
  );
  const totalFixCost = useMemo(
    () =>
      isSensorMode
        ? sensorRangeData?.totalFixedCost ?? fixCosts.reduce((sum, cost) => sum + cost, 0)
        : fixCosts.reduce((sum, cost) => sum + cost, 0),
    [isSensorMode, sensorRangeData, fixCosts]
  );

  const savings = useMemo(
    () =>
      isSensorMode
        ? sensorRangeData?.totalDifference ?? (totalSpotCost !== null ? totalFixCost - totalSpotCost : null)
        : totalSpotCost !== null
          ? totalFixCost - totalSpotCost
          : null,
    [isSensorMode, sensorRangeData, totalSpotCost, totalFixCost]
  );
  const savingsPercent = useMemo(
    () => (savings !== null && totalFixCost > 0 ? (savings / totalFixCost) * 100 : 0),
    [savings, totalFixCost]
  );

  /** Apply the entered fixed price */
  const handleCalculate = useCallback(() => {
    const val =
      typeof fixedPriceInput === 'string' ? parseFloat(fixedPriceInput.replace(',', '.')) : Number(fixedPriceInput);
    if (isNaN(val) || val <= 0) return;

    if (isSensorMode && sensorId) {
      const getSensor = getAction('GetSensor') as any;
      setIsLoadingSensorTariff(true);

      getSensor({
        parameters: { sensorId },
        query: withBrowserTimezone({ fixedTariff: convertPricePerKwhToPerMwh(val) }),
      })
        .then((response: GetSensorResponse) => {
          const fixedPricePerKwh = convertPricePerMwhToPerKwh(
            response.sensor.energyComparison?.fixedTariff ?? convertPricePerKwhToPerMwh(val)
          );
          setSensorEnergyComparison(response.sensor.energyComparison);
          setFixedPriceInput(fixedPricePerKwh);
          setFixedPriceActive(fixedPricePerKwh);
        })
        .finally(() => setIsLoadingSensorTariff(false));

      return;
    }

    setFixedPriceActive(val);
  }, [fixedPriceInput, getAction, isSensorMode, sensorId]);

  const sensorRangeLabels2: Record<SensorRange, string> = {
    dnes: _t('Today'),
    vcera: _t('Yesterday'),
    'tento-tyzden': _t('This week'),
    'tento-mesiac': _t('This month'),
    'poslednych-12-mesiacov': _t('Last 12 months'),
    'minuly-tyzden': _t('Last week'),
    'minuly-mesiac': _t('Last month'),
    'minuly-rok': _t('Last year'),
  };

  const dateLabel = isSensorMode
    ? sensorRangeLabels2[range as SensorRange] ?? _t('Today')
    : selectedDate
      ? selectedDate.toLocaleDateString('sk-SK', { day: 'numeric', month: 'long', year: 'numeric' })
      : _t('Today');

  const rangeSelectData = isSensorMode
    ? [
        { value: 'dnes', label: _t('Today') },
        { value: 'vcera', label: _t('Yesterday') },
        { value: 'tento-tyzden', label: _t('This week') },
        { value: 'minuly-tyzden', label: _t('Last week') },
        { value: 'tento-mesiac', label: _t('This month') },
        { value: 'minuly-mesiac', label: _t('Last month') },
        // { value: 'poslednych-12-mesiacov', label: 'Posledných 12 mesiacov' },
        // { value: 'minuly-rok', label: 'Minulý rok' },
      ]
    : [
        { value: 'dnes', label: _t('Today') },
        { value: 'tyzden', label: _t('Week') },
        { value: 'mesiac', label: _t('This month') },
        { value: 'minuly-mesiac', label: _t('Last month') },
        { value: 'polrok', label: _t('Last half-year') },
        { value: 'rok', label: _t('Last year') },
      ];

  const isSpotBetter = savings !== null && savings > 0;

  // ── Chart configurations ──────────────────────────────────────────────────

  /** Consumption chart: Wh per bar (interval or day) */
  const consumptionChartData = useMemo(() => {
    const barData = isSensorMode
      ? sensorConsumptionData
      : isDay
        ? validIntervals.map((i) => i.consumption || 0)
        : dailySavings.map((d) => d.consumption || 0);
    return {
      labels,
      datasets: [
        {
          label: _t('Consumption (Wh)'),
          data: barData,
          backgroundColor: '#551FFF',
          hoverBackgroundColor: '#4016CC',
          borderRadius: 4,
          borderSkipped: false as const,
          barPercentage: 0.8,
        },
      ],
    };
  }, [labels, isDay, isSensorMode, sensorConsumptionData, validIntervals, dailySavings]);

  const consumptionChartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: { callbacks: { label: (ctx: TooltipItem<'bar'>) => `${ctx.parsed.y.toFixed(1)} Wh` } },
      },
      scales: {
        x: {
          ticks: { color: theme.colors?.gray?.[7], maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          ticks: { color: theme.colors?.gray?.[7], callback: (v: number | string) => `${v} Wh` },
          grid: { color: 'rgba(0,0,0,0.05)' },
          beginAtZero: true,
        },
      },
    }),
    []
  );

  /** Cost chart: cumulative line (Dnes) or grouped bars per day (Týždeň/Mesiac) */
  const dayCostChartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'SPOT',
          data: cumulativeSpotCosts,
          borderColor: '#551FFF',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          borderDash: [] as number[],
        },
        {
          label: 'FIX',
          data: cumulativeFixCosts,
          borderColor: '#E24239',
          backgroundColor: 'transparent',
          borderWidth: 2,
          pointRadius: 0,
          tension: 0.4,
          borderDash: [6, 3] as number[],
        },
      ],
    }),
    [labels, cumulativeSpotCosts, cumulativeFixCosts]
  );

  const dayCostOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: { label: (ctx: TooltipItem<'line'>) => `${ctx.dataset.label}: ${ctx.parsed.y.toFixed(4)} €` },
        },
      },
      scales: {
        x: {
          ticks: { color: theme.colors?.gray?.[7], maxRotation: 0, autoSkip: true, maxTicksLimit: 8 },
          grid: { display: false },
          border: { display: false },
        },
        y: {
          ticks: { color: theme.colors?.gray?.[7], callback: (v: number | string) => `${Number(v).toFixed(2)} €` },
          grid: { color: 'rgba(0,0,0,0.05)' },
          beginAtZero: true,
        },
      },
    }),
    []
  );

  const isLoadingComparison = isLoadingRange || isLoadingSensorTariff;
  const fixedTariffSuffix = '€ / kWh';

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <Stack gap={16}>
      <P2Medium c="gray.10">{_t('Statistics')}</P2Medium>

      {/* ── Consumption chart card ── */}
      <Card p={20} radius={10} shadow="sm">
        <Flex gap={12} mb={16} align="flex-start">
          <Box style={{ flex: 1 }}>
            <P4Regular c="gray.10">{_t('Total consumption')}</P4Regular>
            <H1Medium>{(totalConsumption / 1000).toFixed(1)} kWh</H1Medium>
          </Box>
          <Select
            radius={10}
            styles={{ input: { border: '1px solid #E0E4EC', height: '40px', minWidth: 110 } }}
            value={range}
            onChange={(val) => val && setRange(val as Range)}
            data={rangeSelectData}
          />
        </Flex>

        {/* Legend */}
        <Flex align="center" gap={8} mb={8}>
          <Box w={10} h={10} bg="#551FFF" style={{ borderRadius: '50%' }} />
          <Text size="12px" c="gray.6">
            {_t('Consumption (Wh)')}
          </Text>
        </Flex>

        {/* Bar chart or loader */}
        {isLoadingComparison ? (
          <Flex justify="center" align="center" style={{ height: 220 }}>
            <Loader size="md" color="#551FFF" />
          </Flex>
        ) : (
          <Box style={{ height: 220 }}>
            <Bar data={consumptionChartData} options={consumptionChartOptions} />
          </Box>
        )}
      </Card>

      {/* ── Fix price input — shown when spot data is available ── */}
      {hasSpot && (
        <Card p={20} radius={10} bg="gray.0" style={{ border: '1px solid #E0E4EC' }}>
          <Text fw={600} size="sm" mb={4} c="gray.8">
            {_t('Compare your consumption costs using FIX and SPOT prices')}
          </Text>
          <Text size="xs" c="gray.6" mb={12}>
            {_t('What is your fixed price per kWh?')}
          </Text>
          <Flex gap={8} align="center">
            <NumberInput
              value={fixedPriceInput}
              onChange={setFixedPriceInput}
              min={0}
              max={10}
              step={0.001}
              decimalScale={3}
              rightSection={
                <Text size="xs" c="gray.6" pr={4} style={{ whiteSpace: 'nowrap' }}>
                  {fixedTariffSuffix}
                </Text>
              }
              rightSectionWidth={60}
              style={{ flex: 1 }}
              styles={{ input: { fontSize: '20px', fontWeight: 600, height: '52px' } }}
            />
            <Button
              variant="filled"
              color="violet"
              size="lg"
              radius="md"
              loading={isLoadingSensorTariff}
              onClick={handleCalculate}
              style={{ height: '52px', paddingLeft: 28, paddingRight: 28 }}
            >
              {_t('Calculate')}
            </Button>
          </Flex>
        </Card>
      )}

      {/* ── Cost comparison chart ── */}
      {hasSpot && (
        <Card p={20} radius={10} shadow="sm">
          {/* Header with legend */}
          <Flex justify="space-between" align="center" mb={16}>
            <Text fw={600} size="md">
              {_t('Cost comparison')}
            </Text>
            <Flex gap={16}>
              <Flex align="center" gap={6}>
                <Box w={20} h={2} bg="#551FFF" />
                <Text size="xs" c="gray.6">
                  SPOT
                </Text>
              </Flex>
              <Flex align="center" gap={6}>
                <Box w={20} style={{ height: 0, borderTop: '2px dashed #E24239' }} />
                <Text size="xs" c="gray.6">
                  FIX
                </Text>
              </Flex>
            </Flex>
          </Flex>

          {/* Cumulative line chart for all ranges */}
          {isLoadingComparison ? (
            <Flex justify="center" align="center" style={{ height: 200 }}>
              <Loader size="md" color="#551FFF" />
            </Flex>
          ) : (
            <Box style={{ height: 200 }}>
              <Line data={dayCostChartData} options={dayCostOptions as any} />
            </Box>
          )}

          {/* Metric cards */}
          <Flex gap={12} mt={20}>
            <Flex
              align="center"
              gap={12}
              style={{ flex: 1, borderRadius: 10, padding: '8px 12px', border: '1px solid #E9EBEC' }}
            >
              <Flex
                align="center"
                justify="center"
                style={{ borderRadius: '100%', background: '#F0EBFF', height: '32px', width: '32px' }}
              >
                <IconTrendingUp size={16} color="#551FFF" />
              </Flex>

              <Box>
                <Text size="xs" c="gray.10">
                  {_t('SPOT costs')}
                </Text>
                <Text fw={700} size="lg" c="#0D0E14">
                  {totalSpotCost !== null ? totalSpotCost.toFixed(2) : '—'} €
                </Text>
                <Text size="xs" c="gray.9">
                  {dateLabel}
                </Text>
              </Box>
            </Flex>

            <Flex
              align="center"
              gap={12}
              style={{ flex: 1, borderRadius: 10, padding: '8px 12px', border: '1px solid #E9EBEC' }}
            >
              <Flex
                align="center"
                justify="center"
                style={{ borderRadius: '100%', background: '#FFE8E8', height: '32px', width: '32px' }}
              >
                <IconLock size={18} color="#E24239" />
              </Flex>
              <Box>
                <Text size="xs" c="gray.10">
                  {_t('FIX costs')}
                </Text>
                <Text fw={700} size="lg" c="#0D0E14">
                  {totalFixCost.toFixed(2)} €
                </Text>
                <Text size="xs" c="gray.9">
                  {dateLabel}
                </Text>
              </Box>
            </Flex>
          </Flex>

          {/* Savings bar */}
          {savings !== null && (
            <Box
              mt={12}
              style={{
                borderRadius: 10,
                padding: '12px 16px',
                backgroundColor: isSpotBetter ? '#EAFDEA' : '#FFE8E8',
              }}
            >
              <Flex justify="space-between" align="center">
                <Flex align="center" gap={8}>
                  <Flex
                    align="center"
                    justify="center"
                    style={{
                      borderRadius: '100%',
                      background: isSpotBetter ? '#D2F1D2' : '#ffb7b3',
                      height: '32px',
                      width: '32px',
                    }}
                  >
                    <IconWallet size={20} color={isSpotBetter ? '#119C51' : '#E24239'} />
                  </Flex>

                  <Text fw={600} size="md" c={'#0D0E14'}>
                    {isSpotBetter ? _t('Saved with SPOT') : _t('SPOT is more expensive')}
                  </Text>
                </Flex>
                <Box style={{ textAlign: 'right' }}>
                  <Text fw={700} size="md" c={isSpotBetter ? '#119C51' : '#E24239'}>
                    {Math.abs(savings).toFixed(2)} €
                  </Text>
                  <Text size="sm" c={isSpotBetter ? '#119C51' : '#E24239'}>
                    {Math.abs(savingsPercent).toFixed(0)}% {isSpotBetter ? _t('cheaper') : _t('more expensive')}
                  </Text>
                </Box>
              </Flex>
            </Box>
          )}
        </Card>
      )}
    </Stack>
  );
}
